import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { type OrganizationMessageContentInput } from "../../../generated/graphql";
import {
  contentInput,
  EMPTY_FORM,
  formError,
  type MessageFormState,
} from "../message-content";
import MessageFormFields from "./message-form-fields";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
};

export type FollowUpBlocked = {
  readonly severity: "info" | "warning";
  readonly reason: string;
};

type ResolveDialogProps = {
  readonly open: boolean;
  /** Title of the message being resolved, to seed the follow-up; may be empty. */
  readonly resolvedTitle: string;
  /** Why a follow-up cannot be sent yet, such as its reach still loading. */
  readonly followUpBlocked: FollowUpBlocked | null;
  readonly onClose: () => void;
  /** Resolves when saved; rejects with the reason to show. */
  readonly onSubmit: (
    followUp: OrganizationMessageContentInput | null,
  ) => Promise<void>;
};

/**
 * Ends a live message, optionally posting a follow-up ("resolved, here is
 * what happened") to the same audience in the same step.
 */
const ResolveDialog = ({
  open,
  resolvedTitle,
  followUpBlocked,
  onClose,
  onSubmit,
}: ResolveDialogProps) => {
  const [withFollowUp, setWithFollowUp] = useState(false);
  const [form, setForm] = useState<MessageFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setWithFollowUp(false);
      setForm({
        ...EMPTY_FORM,
        title: resolvedTitle.length > 0 ? `Resolved: ${resolvedTitle}` : "",
      });
      setError(null);
    }
  }, [open, resolvedTitle]);

  const handleSubmit = async () => {
    const problem = withFollowUp ? formError(form) : null;
    if (problem !== null) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(withFollowUp ? contentInput(form) : null);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : String(submitError),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      <DialogTitle>Resolve message</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            Resolving removes the message from every bell and banner.
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={withFollowUp}
                onChange={(event) => {
                  setWithFollowUp(event.target.checked);
                }}
              />
            }
            label="Post a follow-up message to the same audience"
          />
          {withFollowUp && followUpBlocked !== null && (
            <Alert severity={followUpBlocked.severity}>
              {followUpBlocked.reason}
            </Alert>
          )}
          {withFollowUp && <MessageFormFields form={form} onChange={setForm} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || (withFollowUp && followUpBlocked !== null)}
          onClick={() => {
            void handleSubmit();
          }}
        >
          Resolve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResolveDialog;
